import { isRenderedAsIframeInNewUI } from 'config';
import { createContext, useContext, useEffect, useState } from 'react';
import { Coriolis } from '@adobe/coriolis';
import { store } from 'store';

import { generateEPS } from 'pages/ScannerPage/controllers/scannerPageActions';

declare global {
  interface Window {
    cynet: {
      version: string;
    };
  }
}

export type ExposedAPI = {
  fetchDownloadLinks: (osType: number) => Promise<string>;
  getIframeVersion: () => Promise<{ version: string }>;
};

const exposedAPI: ExposedAPI = {
  fetchDownloadLinks: async (osType: number) => store.dispatch(generateEPS(osType)),
  getIframeVersion: async () => window.cynet
};

const iframeMessageListenerContext = createContext<{
  setHandler: (messageName: string, handler: (data: any) => void) => void;
  connection: Coriolis | undefined;
} | null>(null);

export const IframeMessageListenerProvider = ({ children }: { children: React.ReactNode }) => {
  const [messageNameToHandlerMap, setMessageNameToHandlerMap] = useState<Record<string, (data: any) => void>>({});
  const [connection, setConnection] = useState<Coriolis>();

  useEffect(() => {
    if (isRenderedAsIframeInNewUI) {
      // TODO: for the better security replace check ancestorOrigins to be one of allowed URLs list.
      // It will allow only the parent window to communicate with the iframe.
      // consider setting only allowed URL through env vars during the deploy.
      let ancestorUrl;
      if (window.location.ancestorOrigins) {
        // chromium based browsers support ancestorOrigins
        ancestorUrl = window.location.ancestorOrigins[0];
      } else if (window.document.referrer && window.document.referrer !== '') {
        // other browsers will try to derive the ancestor URL from the referrer
        ancestorUrl = new URL(window.document.referrer).origin;
      } else {
        // fallback to the proxy URL if no ancestor URL can be determined
        ancestorUrl = process.env.REACT_APP_PROXY;
      }

      if (!ancestorUrl) {
        console.warn(
          'Cannot determine Host Application URL for interframe connection. Iframe communication may not work as expected.'
        );
      }

      const connectionInstance = new Coriolis(window.parent, ancestorUrl || '');

      Object.entries(exposedAPI).forEach(([key, value]) => {
        connectionInstance.query.register(key, value);
      });

      setConnection(connectionInstance);

      return () => connectionInstance?.disconnect();
    }

    return () => {};
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data;
      if (messageNameToHandlerMap[type]) {
        messageNameToHandlerMap[type](payload);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [messageNameToHandlerMap]);

  return (
    <iframeMessageListenerContext.Provider
      value={{
        setHandler: (messageName, handler) => {
          setMessageNameToHandlerMap(prev => ({ ...prev, [messageName]: handler }));
        },
        connection
      }}
    >
      {children}
    </iframeMessageListenerContext.Provider>
  );
};

export const useIframeCommunication = () => {
  const context = useContext(iframeMessageListenerContext);

  if (!isRenderedAsIframeInNewUI) {
    return {
      connection: undefined
    };
  }

  if (!context) {
    throw new Error('useIframeCommunication must be used within a IframeMessageListenerProvider');
  }

  return {
    connection: context.connection
  };
};

export const useIframeMessageListener = (messageName: string, handler: (data: any) => void) => {
  const context = useContext(iframeMessageListenerContext);

  useEffect(() => {
    if (!isRenderedAsIframeInNewUI) {
      return;
    }

    if (!context) {
      throw new Error('useIframeMessageListener must be used within a IframeMessageListenerProvider');
    }

    context.setHandler(messageName, handler);
  }, [handler, messageName]);
};
